import { registerServiceWorker } from './deviceNotifications';
import { initPushNotifications } from './pushNotifications';

const PERIODIC_TAG = 'hris-background';
const SYNC_TAG = 'hris-sync';
const MIN_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 jam (Chrome minimum ~12h di beberapa perangkat)

let started = false;
let visibilityBound = false;

/**
 * Daftarkan Periodic Background Sync agar SW dijadwalkan di latar belakang
 * (Chrome/Android, PWA terpasang, izin notifikasi granted).
 */
export async function registerBackgroundSync(registration) {
  if (!registration) return false;

  try {
    if ('periodicSync' in registration) {
      const status = await navigator.permissions?.query?.({ name: 'periodic-background-sync' });
      if (!status || status.state === 'granted') {
        await registration.periodicSync.register(PERIODIC_TAG, {
          minInterval: MIN_INTERVAL_MS,
        });
      }
    }
  } catch (err) {
    console.warn('[pwaBackground] periodicSync unavailable:', err?.message || err);
  }

  try {
    if ('sync' in registration) {
      await registration.sync.register(SYNC_TAG);
    }
  } catch {
    /* Background Sync opsional */
  }

  return true;
}

/**
 * Beritahu SW saat app masuk latar belakang agar tetap di-claim.
 */
function pingServiceWorker() {
  try {
    const controller = navigator.serviceWorker?.controller;
    controller?.postMessage({ type: 'HRIS_KEEPALIVE', at: Date.now() });
  } catch {
    /* ignore */
  }
}

/**
 * Aktifkan mode latar belakang PWA:
 * - pastikan SW terdaftar
 * - daftar periodic/background sync
 * - jaga push subscription saat app di-minimize / ditutup
 */
export async function enablePwaBackground({ withPush = true } = {}) {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;

  const registration = await registerServiceWorker();
  if (!registration) return null;

  await registerBackgroundSync(registration);

  if (withPush && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    await initPushNotifications({ force: true });
  }

  if (!visibilityBound) {
    visibilityBound = true;

    const onHidden = () => {
      if (document.visibilityState === 'hidden') {
        pingServiceWorker();
        registerBackgroundSync(registration).catch(() => {});
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          initPushNotifications().catch(() => {});
        }
      }
    };

    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', () => {
      pingServiceWorker();
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        initPushNotifications().catch(() => {});
      }
    });

    // Beberapa browser mem-freeze tab di latar belakang
    document.addEventListener('freeze', pingServiceWorker);
  }

  started = true;
  return registration;
}

/**
 * Boot awal: daftarkan SW secepat mungkin (tanpa menunggu login).
 */
export function bootPwaBackground() {
  if (typeof window === 'undefined' || started) return;
  if (!('serviceWorker' in navigator)) return;

  const run = () => {
    enablePwaBackground({ withPush: false }).catch((err) => {
      console.warn('[pwaBackground] boot failed:', err);
    });
  };

  if (document.readyState === 'complete') {
    run();
  } else {
    window.addEventListener('load', run, { once: true });
  }
}
